import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { DateType } from '../../CommonTypes';
import ShoppingListMarkProductAsBoughtMutation from './ShoppingListMarkProductAsBoughtMutation';
import optimisticResponseBought from './ShoppingListMarkProductAsBoughtOptimisticResponse';
import optimisticResponseNotBought from './ShoppingListMarkProductAsNotBoughtOptimisticResponse';
import ShoppingListMarkProductAsNotBoughtMutation from './ShoppingListMarkProductAsNotBoughtMutation';

const MARK = 'mark';
const UNMARK = 'unMark';

const ShoppingListMarkProductConnectorApollo = ({ children, period }) => {
  const [mutateBought] = useMutation(ShoppingListMarkProductAsBoughtMutation);
  const [mutateNotBought] = useMutation(ShoppingListMarkProductAsNotBoughtMutation);

  const handleMarkProduct = async (markValueSign, { key, productId, __typename }) => {
    if (markValueSign !== MARK && markValueSign !== UNMARK) {
      throw new Error('Invalid markValue. Enter "mark" or "unMark"');
    }
    if (!period) {
      throw new Error('Tried to mark product as bought with undefined period');
    }

    const { start, end } = period;

    const variables = {
      request: {
        productId,
        datePeriod: { start, end },
      },
    };

    if (markValueSign === MARK) {
      await mutateBought({
        variables,
        optimisticResponse: markValueSign === optimisticResponseBought,
        update: (cache, { data }) => {
          const { isBought } = data.me.shoppingListMarkProductAsBought;

          const cachedProductToFindById = {
            __typename,
            key,
          };
          cache.modify({
            id: cache.identify(cachedProductToFindById),
            fields: {
              isBought() {
                return isBought;
              },
            },
          });
        },
      });
    } else {
      await mutateNotBought({
        variables,
        optimisticResponse: markValueSign === optimisticResponseNotBought,
        update: (cache, { data }) => {
          const { isBought } = data.me.shoppingListMarkProductAsNotBought;

          const cachedProductToFindById = {
            __typename,
            key,
          };
          cache.modify({
            id: cache.identify(cachedProductToFindById),
            fields: {
              isBought() {
                return isBought;
              },
            },
          });
        },
      });
    }
  };

  return children({
    handleMarkProduct,
  });
};

ShoppingListMarkProductConnectorApollo.propTypes = {
  children: PropTypes.func.isRequired,
  period: PropTypes.shape({
    start: DateType,
    end: DateType,
  }),
};

ShoppingListMarkProductConnectorApollo.defaultProps = {
  period: undefined,
};

export default ShoppingListMarkProductConnectorApollo;
